import React, { useMemo } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { SalvButton, SalvDropdown, SalvModal, SalvTable, Size } from '@salvapp/ui-components';
import * as Yup from 'yup';
import { useQueryEntitySchema } from '../../../schema/entitySchemaActions';
import { InternalApiEntityType } from '../../../api-internal';
import { createColumnHelper } from '@tanstack/react-table';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import { ApiScreeningPersonScreeningContextConfiguration, personScreeningContextConfigurationApi } from '../../../apis';
import { isVisible } from '../../../schema/helper';

const validationSchema = Yup.object({
  configuration: Yup.array().of(
    Yup.object().shape({
      fieldName: Yup.string().required('Field is required'),
    })
  ),
});

interface FieldName {
  fieldName: string;
}

interface FormValues {
  configuration: FieldName[];
}

interface PersonSignificantUpdateIndicatorModalProps {
  fieldNames: string[];
  updatePersonSignificantFields: (significantFields: ApiScreeningPersonScreeningContextConfiguration[]) => void;
  handleClose: () => void;
  show: boolean;
}

export const PersonSignificantUpdateIndicatorModal = ({
  fieldNames,
  updatePersonSignificantFields,
  handleClose,
  show,
}: PersonSignificantUpdateIndicatorModalProps) => {
  const { data: personSchema } = useQueryEntitySchema(InternalApiEntityType.PERSON);
  const schemaFields = personSchema?.schemaFields.filter((field) => isVisible(field));
  const dispatch = useDispatch();

  const fieldMap: { [key: string]: string } = useMemo(() => {
    const parentFields = schemaFields?.filter((field) => !field.parentId) ?? [];
    return (
      schemaFields?.reduce((acc, field) => {
        const parentField = field.parentId ? parentFields.find((parent) => parent.id === field.parentId) : null;
        const displayName = field.displayName;
        let fieldName = field.fieldName;
        if (parentField) {
          const separator = parentField.isMulti ? '[].' : '.';
          fieldName = parentField.fieldName + separator + field.fieldName;
        }
        return {
          ...acc,
          [fieldName]: displayName,
        };
      }, {}) ?? {}
    );
  }, [schemaFields]);

  const dropdownOptions: DropdownOption[] = useMemo(() => {
    if (!fieldMap) {
      return [];
    }
    return Object.entries(fieldMap).map(([key, value]) => ({ key, label: `${value} (${key})`, value: key }));
  }, [fieldMap]);

  const initialValues: FormValues = fieldNames?.length
    ? { configuration: [...fieldNames].map((field) => ({ fieldName: field })) }
    : { configuration: [{ fieldName: '' }] };

  const columnHelper = createColumnHelper<FieldName>();
  const columns = useMemo(
    () => (values: FormValues, setFieldValue: FormikHelpers<FormValues>['setFieldValue']) => [
      columnHelper.accessor('fieldName', {
        header: 'Field',
        cell: ({ row, table, getValue }) => {
          const getOptions = () => {
            const selectedFieldNames = [...table.getRowModel().rows.map((row) => row.original)].map(
              (field) => field.fieldName
            );
            return dropdownOptions.filter((option) => {
              return !selectedFieldNames.includes(option.key) || option.key === getValue();
            });
          };
          const onChange = (option: DropdownOption[]) => {
            const values = [...table.getRowModel().rows.map((row) => row.original)];
            values.splice(row.index, 1, { fieldName: option[0].value });
            setFieldValue('configuration', [...values]);
          };
          if (getValue() && dropdownOptions.find((option) => option.key === getValue()) === undefined) {
            return (
              <div>
                The field
                <b> {getValue()} </b>
                does not exist in your schema or is not visible. Delete this configuration row or update your Schema.
              </div>
            );
          } else {
            return (
              <SalvDropdown
                ariaLabel={`Field ${row.index + 1}`}
                options={getOptions()}
                value={dropdownOptions.find((option) => option.key === getValue())}
                clearable={false}
                size={Size.SMALL}
                onChange={onChange}
                renderedInBootstrapModal
              />
            );
          }
        },
      }),
      columnHelper.display({
        id: 'remove',
        cell: ({ row, table }) => {
          const removeRow = () => {
            const newValues = [...table.getRowModel().rows.map((row) => row.original)];
            newValues.splice(row.index, 1);
            setFieldValue('configuration', [...newValues]);
          };
          return (
            <SalvButton
              label='Remove'
              ariaLabel={`Remove field ${row.index + 1}`}
              variant='danger-outline'
              size={Size.SMALL}
              onClick={removeRow}
            />
          );
        },
        size: 0,
      }),
    ],
    [dropdownOptions]
  );

  const submit = (values: FormValues, { setSubmitting, resetForm }: FormikHelpers<FormValues>) => {
    dispatch(
      showConfirmationModal({
        title: 'Are you sure?',
        description:
          "Any changes to screening configuration can have a serious impact on your company's AML workload and business success",
        onConfirm: async () => {
          const response = await personScreeningContextConfigurationApi.updatePersonScreeningContext({
            apiScreeningPersonScreeningContextConfiguration: values.configuration.map((field) => ({
              field: field.fieldName,
              normalisable: true,
            })),
          });
          updatePersonSignificantFields(response);

          handleClose();
          resetForm();
        },
        confirmationText: 'Yes',
        cancelText: 'No, go back',
      })
    ).then(() => {
      setSubmitting(false);
    });
  };

  return (
    <Formik
      onSubmit={submit}
      validationSchema={validationSchema}
      enableReinitialize={true}
      initialValues={initialValues}
    >
      {({ isSubmitting, errors, values, setFieldValue }: FormikProps<FormValues>) => {
        return (
          <SalvModal className='person-significant-update-indicator-modal' show={show} onHide={handleClose}>
            <Form>
              <SalvModal.Header>Edit Significant Update Indicators </SalvModal.Header>
              <SalvModal.Body>
                <SalvTable
                  columns={columns(values, setFieldValue)}
                  data={values.configuration}
                  emptyMessage={'No child fields defined yet'}
                  striped
                />
                <SalvButton
                  label='Add field'
                  variant='secondary'
                  size={Size.SMALL}
                  icon={faPlus}
                  className='m-t-8'
                  onClick={() => {
                    setFieldValue('configuration', [...values.configuration, { fieldName: '' }]);
                  }}
                />
              </SalvModal.Body>
              <SalvModal.Footer>
                <SalvButton label='Close' variant='muted' size={Size.LARGE} onClick={handleClose} />
                <SalvButton
                  label='Save'
                  variant='primary'
                  size={Size.LARGE}
                  type='submit'
                  disabled={Boolean(isSubmitting || Object.keys(errors).length)}
                />
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default PersonSignificantUpdateIndicatorModal;
